import { useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Gauge, ThumbsUp, ThumbsDown, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import PlagiarismChecker from "@/components/PlagiarismChecker";

const SUCCESS_IMG =
  "https://images.pexels.com/photos/8423008/pexels-photo-8423008.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Assessment() {
  return (
    <Layout title="AI Assessment Engine">
      <Tabs defaultValue="grade">
        <TabsList className="rounded-full bg-muted p-1">
          <TabsTrigger value="grade" className="rounded-full px-4" data-testid="tab-grade">
            <Gauge className="h-3.5 w-3.5 mr-1.5" /> Auto-grade
          </TabsTrigger>
          <TabsTrigger value="plagiarism" className="rounded-full px-4" data-testid="tab-plagiarism">
            <ShieldAlert className="h-3.5 w-3.5 mr-1.5" /> Plagiarism & AI
          </TabsTrigger>
        </TabsList>
        <TabsContent value="grade" className="mt-5">
          <GradePanel />
        </TabsContent>
        <TabsContent value="plagiarism" className="mt-5">
          <PlagiarismChecker />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}

function GradePanel() {
  const [form, setForm] = useState({
    question: "Explain the process of photosynthesis in your own words.",
    student_answer: "",
    rubric: "",
    max_marks: 10,
    grade: "9",
    subject: "Biology",
    language: "English",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.student_answer.trim()) {
      toast.error("Please paste the student's answer");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/assessment/evaluate", {
        ...form,
        max_marks: parseInt(form.max_marks) || 10,
      });
      setResult(data);
      toast.success("Evaluation complete");
    } catch {
      toast.error("Evaluation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      <div className="lg:col-span-2 tb-card p-6 space-y-4" data-testid="assessment-form">
        <div>
          <Label>Question</Label>
          <Textarea
            rows={3}
            value={form.question}
            onChange={(e) => update("question", e.target.value)}
            className="mt-1.5 rounded-xl"
            data-testid="assessment-question"
          />
        </div>
        <div>
          <Label>Student answer</Label>
          <Textarea
            rows={6}
            value={form.student_answer}
            onChange={(e) => update("student_answer", e.target.value)}
            placeholder="Paste the student's answer here…"
            className="mt-1.5 rounded-xl"
            data-testid="assessment-answer"
          />
        </div>
        <div>
          <Label>Rubric (optional)</Label>
          <Textarea
            rows={3}
            value={form.rubric}
            onChange={(e) => update("rubric", e.target.value)}
            placeholder="e.g. Accuracy 40%, Completeness 30%, Clarity 20%, Reasoning 10%"
            className="mt-1.5 rounded-xl"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Max marks</Label>
            <Input
              type="number"
              value={form.max_marks}
              onChange={(e) => update("max_marks", e.target.value)}
              className="mt-1.5 h-11 rounded-xl"
              data-testid="assessment-max-marks"
            />
          </div>
          <div>
            <Label>Grade</Label>
            <Input
              value={form.grade}
              onChange={(e) => update("grade", e.target.value)}
              className="mt-1.5 h-11 rounded-xl"
            />
          </div>
          <div>
            <Label>Subject</Label>
            <Input
              value={form.subject}
              onChange={(e) => update("subject", e.target.value)}
              className="mt-1.5 h-11 rounded-xl"
            />
          </div>
        </div>
        <Button
          onClick={submit}
          disabled={busy}
          className="w-full h-11 rounded-full"
          data-testid="assessment-evaluate-button"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Gauge className="h-4 w-4 mr-1.5" /> Evaluate</>}
        </Button>
      </div>

      <div className="lg:col-span-3 tb-card p-6 min-h-[480px]" data-testid="assessment-result">
        {!result && !busy && (
          <div className="h-full grid place-items-center text-center text-muted-foreground py-20">
            <div>
              <Gauge className="h-8 w-8 mx-auto opacity-50" />
              <p className="mt-3 font-display">Grading results will appear here.</p>
            </div>
          </div>
        )}
        {busy && (
          <div className="h-full grid place-items-center text-center text-muted-foreground py-20">
            <div>
              <Loader2 className="h-8 w-8 animate-spin mx-auto" />
              <p className="mt-3 font-display">Evaluating with TeeBee Intelligence…</p>
            </div>
          </div>
        )}
        {result && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="relative">
                <div className="h-28 w-28 rounded-full grid place-items-center bg-gradient-to-br from-emerald-200 to-violet-200 dark:from-emerald-500/30 dark:to-violet-500/30">
                  <span className="font-display text-3xl font-medium" data-testid="assessment-score">
                    {result.score}<span className="text-muted-foreground text-sm font-body">/{result.max_marks}</span>
                  </span>
                </div>
                <img src={SUCCESS_IMG} alt="" aria-hidden className="absolute -right-3 -top-3 h-10 w-10 hidden sm:block" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">AI feedback</p>
                <p className="mt-2 text-sm leading-relaxed">{result.feedback}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10 p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700 dark:text-emerald-300">
                  <ThumbsUp className="h-3.5 w-3.5" /> Strengths
                </div>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {(result.strengths || []).map((s, i) => (
                    <li key={`s-${i}-${(s || '').slice(0, 24)}`}>• {s}</li>
                  ))}
                  {(result.strengths || []).length === 0 && <li className="text-muted-foreground">—</li>}
                </ul>
              </div>
              <div className="rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700 dark:text-amber-300">
                  <ThumbsDown className="h-3.5 w-3.5" /> Improvements
                </div>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {(result.improvements || []).map((s, i) => (
                    <li key={`i-${i}-${(s || '').slice(0, 24)}`}>• {s}</li>
                  ))}
                  {(result.improvements || []).length === 0 && <li className="text-muted-foreground">—</li>}
                </ul>
              </div>
            </div>

            {result.rubric_breakdown && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Rubric breakdown</p>
                <Markdown className="mt-2">{result.rubric_breakdown}</Markdown>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
